import { Avatar, AvatarFallback, AvatarImage } from "@choose/ui/avatar";
import { Separator } from "@choose/ui/separator";
import { Skeleton } from "@choose/ui/skeleton";
import { SearchXIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useInternalUsersQuery } from "~/graphql/graphql.gen";

export function UserPreview({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const { data: agents, isLoading } = useInternalUsersQuery(
    {},
    {
      select: (data) => data.internalUsers,
    },
  );

  const user = agents?.find((agent) => agent.id === userId);

  if (isLoading)
    return (
      <div role="status" className="flex flex-col gap-2">
        <Skeleton className="mb-3 h-2.5 w-48" />

        <div className="space-y-2">
          <Skeleton className="mb-2.5 h-2 max-w-[360px]" />
          <Skeleton className="h-2" />
        </div>

        <Separator className="my-2" />

        <div className="space-y-2">
          <Skeleton className="mb-2.5 h-2 max-w-[330px]" />
          <Skeleton className="h-2 max-w-[300px]" />
        </div>

        <Separator className="my-2" />

        <div className="space-y-2">
          <Skeleton className="mb-2.5 h-2 max-w-[330px]" />
          <Skeleton className="h-2 max-w-[300px]" />
        </div>

        <Separator className="my-2" />

        <Skeleton className="h-2 max-w-[360px]" />

        <span className="sr-only">{t("user_preview.loading")}</span>
      </div>
    );

  if (!user)
    return (
      <div className="flex items-center justify-center">
        <div className="text-foreground flex items-center gap-2 text-sm">
          <SearchXIcon className="size-4" />
          <span>{t("user_preview.not_found")}</span>
        </div>
      </div>
    );

  return (
    <div className="flex flex-col gap-2">
      <Avatar className="size-16 shrink-0">
        <AvatarImage src={user.internalUser.picture} />
        <AvatarFallback className="text-lg">
          {user.internalUser.firstName.charAt(0)}
        </AvatarFallback>
      </Avatar>
      <h1 className="text-foreground font-semibold">
        {user.internalUser.firstName} {user.internalUser.lastName}
      </h1>

      <Separator className="my-2" />

      <p className="text-muted-foreground flex gap-1 text-sm">
        {user.internalUser.email}
      </p>
    </div>
  );
}
